/**
 * Number and Value Formatters
 *
 * Utility functions for formatting numbers, currencies, and handling zero suppression
 * Matches Angular's DecimalPipe and SuppressZero pipe behavior
 */

/**
 * Suppress zero values - returns empty string for zero/null/undefined
 * Matches Angular's SuppressZero pipe behavior
 */
export function suppressZero(value: number | null | undefined, placeholder = ''): string {
  if (value === null || value === undefined || value === 0) {
    return placeholder;
  }
  return value.toString();
}

/**
 * Format number with specified decimal places
 * Matches Angular's DecimalPipe format: 'minInteger.minFraction-maxFraction'
 *
 * @param value - Number to format
 * @param minDecimals - Minimum decimal places (default: 0)
 * @param maxDecimals - Maximum decimal places (default: 0)
 * @param suppressZeroValue - If true, returns empty string for zero values
 * @returns Formatted number string
 */
export function formatNumber(
  value: number | null | undefined,
  minDecimals = 0,
  maxDecimals = 0,
  suppressZeroValue = false
): string {
  if (value === null || value === undefined) {
    return '';
  }

  if (suppressZeroValue && value === 0) {
    return '';
  }

  return value.toLocaleString('en-US', {
    minimumFractionDigits: minDecimals,
    maximumFractionDigits: maxDecimals,
  });
}

/**
 * Format currency (USD)
 */
export function formatCurrency(
  value: number | null | undefined,
  includeSymbol = true,
  decimals = 0
): string {
  if (value === null || value === undefined) {
    return '';
  }

  const formatted = formatNumber(value, decimals, decimals);
  return includeSymbol ? `$${formatted}` : formatted;
}

/**
 * Format percentage
 */
export function formatPercent(value: number | null | undefined, decimals = 2): string {
  if (value === null || value === undefined) {
    return '';
  }

  // Assuming value is already in percentage form (e.g., 0.62 for 0.62%)
  // If it's a decimal (e.g., 0.0062 for 0.62%), multiply by 100
  const percentValue = value < 1 && value > -1 && value !== 0 ? value * 100 : value;
  return `${formatNumber(percentValue, decimals, decimals)}%`;
}

/**
 * Format offhire display (combines days and percentage)
 * Matches Angular template: totalOffhire + ' / ' + percentOffhire + '%'
 */
export function formatOffhire(days: number, percent: number): string {
  return `${formatNumber(days, 0, 0)} / ${formatPercent(percent, 2)}`;
}

/**
 * Format days/hours/minutes display
 */
export function formatDaysHoursMinutes(days?: number, hours?: number, minutes?: number): string {
  const parts: string[] = [];
  if (days) parts.push(`${days}d`);
  if (hours) parts.push(`${hours}h`);
  if (minutes) parts.push(`${minutes}m`);
  return parts.join(' ') || '-';
}

/**
 * Format date for display
 */
export function formatDate(date: string | Date | null | undefined, format = 'MMM-yy'): string {
  if (!date) return '';

  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';

  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const month = monthNames[d.getMonth()];
  const year = d.getFullYear().toString().slice(-2);

  if (format === 'MMM-yy') {
    return `${month}-${year}`;
  }

  return d.toLocaleDateString();
}
